import { useState } from 'react';
import {
  Banknote,
  CreditCard,
  Globe2,
  HelpCircle,
  Info,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Drawer } from '../../components/Drawer';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { cn } from '../../lib/cn';
import { formatDate } from '../../lib/format';
import type {
  CurrencyCode,
  GatewayMode,
  GatewaySlug,
  PaymentGatewayDetailed,
} from '../../mocks/clientBatch';
import { useCurrencies, usePaymentGateways } from '../../api/platform';

const GATEWAY_ICON: Record<GatewaySlug, LucideIcon> = {
  sslcommerz: Wallet,
  stripe: CreditCard,
  paypal: Globe2,
  manual_cash: Banknote,
};

const GATEWAY_ACCENT: Record<GatewaySlug, string> = {
  sslcommerz:
    'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300',
  stripe:
    'bg-violet-100 dark:bg-violet-950/60 text-violet-700 dark:text-violet-300',
  paypal:
    'bg-sky-100 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300',
  manual_cash:
    'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300',
};

const MODE_DOT: Record<GatewayMode, string> = {
  live: 'bg-emerald-500',
  sandbox: 'bg-amber-500',
  disabled: 'bg-zinc-400 dark:bg-zinc-500',
};

const MODE_LABEL: Record<GatewayMode, string> = {
  live: 'Connected',
  sandbox: 'Test mode',
  disabled: 'Disabled',
};

const MODE_TEXT: Record<GatewayMode, string> = {
  live: 'text-emerald-700 dark:text-emerald-300',
  sandbox: 'text-amber-700 dark:text-amber-300',
  disabled: 'text-zinc-500 dark:text-zinc-400',
};

/**
 * Payment gateways — three tile grid (SSLCommerz / Stripe / PayPal) plus
 * the manual cash row. Each tile shows mode, supported currencies, last
 * charge, this-month volume, and a Configure drawer.
 */
export function Gateways() {
  const { data: paymentGatewaysDetailed = [] } = usePaymentGateways();
  // Show the three external gateways as tiles. Manual cash is documented
  // in the routing card below — its config is at /admin/cash-entry.
  const tileGateways = paymentGatewaysDetailed.filter(
    (g) => g.slug !== 'manual_cash',
  );

  const [openSlug, setOpenSlug] = useState<GatewaySlug | null>(null);
  const activeGateway =
    paymentGatewaysDetailed.find((g) => g.slug === openSlug) ?? null;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="Payment gateways"
        subtitle="Configure how customers can pay. Currency drives gateway routing at checkout."
        action={
          <Button
            variant="secondary"
            size="md"
            disabled
            title="All supported gateways are listed below — to add a new provider, contact engineering."
          >
            + Add gateway
          </Button>
        }
      />

      {/* Three tiles */}
      <div className="grid sm:grid-cols-3 gap-4">
        {tileGateways.map((g) => (
          <GatewayTile
            key={g.slug}
            gateway={g}
            onConfigure={() => setOpenSlug(g.slug)}
          />
        ))}
      </div>

      {/* Routing rule info card */}
      <Card className="mt-6 p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <span className="shrink-0 w-9 h-9 grid place-items-center rounded-md bg-sky-100 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300">
            <Info className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">
              Checkout routing rule
            </h3>
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
              At checkout, the platform picks a gateway based on the invoice's currency:
            </p>
            <ul className="mt-2.5 text-sm text-zinc-700 dark:text-zinc-200 space-y-1">
              <li className="flex items-start gap-2">
                <span className="text-zinc-400 dark:text-zinc-500">•</span>
                <span>
                  <span className="font-medium">BDT</span> → SSLCommerz only (manual cash also available)
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-zinc-400 dark:text-zinc-500">•</span>
                <span>
                  <span className="font-medium">USD / GBP / EUR</span> → Stripe primary, PayPal as fallback
                </span>
              </li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Configure drawer */}
      <Drawer
        open={openSlug !== null}
        onClose={() => setOpenSlug(null)}
        title={
          activeGateway
            ? `Configure ${activeGateway.displayName}`
            : 'Configure gateway'
        }
        width="md"
      >
        {activeGateway && (
          <ConfigureForm
            gateway={activeGateway}
            onClose={() => setOpenSlug(null)}
          />
        )}
      </Drawer>
    </div>
  );
}

function GatewayTile({
  gateway,
  onConfigure,
}: {
  gateway: PaymentGatewayDetailed;
  onConfigure: () => void;
}) {
  const Icon = GATEWAY_ICON[gateway.slug];

  // Pick a primary currency for the this-month total. Prefer the gateway's
  // declared primary; fall back to the first supported currency.
  const primaryCurrency: CurrencyCode =
    gateway.isPrimaryFor?.[0] ?? gateway.supportedCurrencies[0] ?? 'BDT';

  return (
    <Card className="p-5 sm:p-6 flex flex-col">
      {/* Header: icon + name + mode badge */}
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'shrink-0 w-10 h-10 grid place-items-center rounded-md',
            GATEWAY_ACCENT[gateway.slug],
          )}
        >
          <Icon className="w-5 h-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
            {gateway.displayName}
          </h3>
          <div className="mt-0.5 flex items-center gap-1.5">
            <span
              className={cn(
                'inline-block w-1.5 h-1.5 rounded-full',
                MODE_DOT[gateway.mode],
              )}
            />
            <span className={cn('text-xs font-medium', MODE_TEXT[gateway.mode])}>
              {MODE_LABEL[gateway.mode]}
            </span>
          </div>
        </div>
      </div>

      <p className="mt-4 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">
        {gateway.description}
      </p>

      {/* Currencies */}
      <div className="mt-4">
        <span className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
          Currencies
        </span>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {gateway.supportedCurrencies.map((code) => (
            <CurrencyPill key={code} code={code} />
          ))}
        </div>
      </div>

      {/* Last charge */}
      <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700/70 text-sm">
        <div className="flex items-center justify-between gap-2">
          <span className="text-zinc-500 dark:text-zinc-400">Last charge</span>
          {gateway.lastChargeAt &&
          gateway.lastChargeAmount !== null &&
          gateway.lastChargeCurrency ? (
            <span className="flex items-center gap-2 text-zinc-700 dark:text-zinc-200">
              <span className="tabular-nums text-xs">
                {formatDate(gateway.lastChargeAt, { compact: true })}
              </span>
              <Money
                minor={gateway.lastChargeAmount}
                currencyCode={gateway.lastChargeCurrency}
                hideCents
                tone="neutral"
                className="text-sm font-medium text-zinc-900 dark:text-zinc-100"
              />
            </span>
          ) : (
            <span className="text-zinc-500 dark:text-zinc-400 text-xs">
              No charges yet
            </span>
          )}
        </div>

        <div className="mt-2 flex items-start justify-between gap-2">
          <span className="text-zinc-500 dark:text-zinc-400">This month</span>
          <div className="text-right">
            <div className="text-zinc-700 dark:text-zinc-200 text-xs tabular-nums">
              {gateway.totalChargesThisMonth.toLocaleString('en-US')} charges
            </div>
            {gateway.totalAmountThisMonth > 0 ? (
              <Money
                minor={gateway.totalAmountThisMonth}
                currencyCode={primaryCurrency}
                hideCents
                tone="neutral"
                className="text-sm font-semibold text-zinc-900 dark:text-zinc-100"
              />
            ) : (
              <span className="text-xs text-zinc-500 dark:text-zinc-400">—</span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-5">
        <Button variant="secondary" size="sm" fullWidth onClick={onConfigure}>
          Configure
        </Button>
      </div>
    </Card>
  );
}

function CurrencyPill({ code }: { code: CurrencyCode }) {
  const { data: currencies = [] } = useCurrencies();
  const meta = currencies.find((c) => c.code === code);
  if (!meta) return null;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border bg-zinc-50 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700">
      <span aria-hidden="true">{meta.flag}</span>
      <span>{meta.code}</span>
    </span>
  );
}

/* ── Configure form ─────────────────────────────────────────────────── */

function ConfigureForm({
  gateway,
  onClose,
}: {
  gateway: PaymentGatewayDetailed;
  onClose: () => void;
}) {
  const [saving, setSaving] = useState(false);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSaving(true);
    // Simulate save — no real persistence in mock.
    setTimeout(() => {
      setSaving(false);
      onClose();
    }, 700);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-zinc-600 dark:text-zinc-300">
        {gateway.description}
      </p>

      {gateway.slug === 'sslcommerz' && <SSLCommerzFields />}
      {gateway.slug === 'stripe' && <StripeFields />}
      {gateway.slug === 'paypal' && <PayPalFields />}
      {gateway.slug === 'manual_cash' && <ManualCashInfo />}

      {gateway.slug !== 'manual_cash' && (
        <div className="pt-2 flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="md" loading={saving}>
            Save changes
          </Button>
        </div>
      )}

      {gateway.slug === 'manual_cash' && (
        <div className="pt-2 flex items-center justify-end">
          <Button type="button" variant="secondary" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      )}
    </form>
  );
}

function SSLCommerzFields() {
  return (
    <>
      <Input
        label="Store ID"
        placeholder="itcitysoft01"
        defaultValue="itcitysoft01"
        required
      />
      <Input
        label="Store password"
        type="password"
        placeholder="••••••••"
        defaultValue="mockpass-1234"
        required
      />
      <Input
        label="Merchant ID"
        placeholder="MERCHANT-7842"
        defaultValue="MERCHANT-7842"
      />
      <SandboxToggle defaultEnabled={false} />
      <p className="text-xs text-zinc-500 dark:text-zinc-400">
        IPN endpoint:{' '}
        <code className="mono px-1.5 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200">
          /webhooks/sslcommerz/ipn
        </code>
      </p>
    </>
  );
}

function StripeFields() {
  const [pubKey, setPubKey] = useState('pk_live_51HxxxxBKxxxx');
  const inferredSandbox = pubKey.startsWith('pk_test_');
  return (
    <>
      <Input
        label="Publishable key"
        placeholder="pk_live_… or pk_test_…"
        value={pubKey}
        onChange={(e) => setPubKey(e.target.value)}
        hint="Sandbox mode is inferred automatically from the key prefix."
        required
      />
      <Input
        label="Secret key"
        type="password"
        placeholder="sk_live_… or sk_test_…"
        defaultValue="sk_live_xxxxxxxx"
        required
      />
      <Input
        label="Webhook signing secret"
        type="password"
        placeholder="whsec_…"
        defaultValue="whsec_mockxxxxxxxx"
        required
      />
      <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border border-zinc-200 dark:border-zinc-700 bg-zinc-50/60 dark:bg-zinc-800/40">
        <span className="text-sm text-zinc-700 dark:text-zinc-200">
          Sandbox mode (auto-detected)
        </span>
        <span
          className={cn(
            'text-xs font-medium px-2 py-0.5 rounded-full border',
            inferredSandbox
              ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800'
              : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800',
          )}
        >
          {inferredSandbox ? 'Test mode' : 'Live'}
        </span>
      </div>
    </>
  );
}

function PayPalFields() {
  return (
    <>
      <Input
        label="Client ID"
        placeholder="AeA1QIZXiflr…"
        defaultValue="AeA1QIZXiflr_mock_clientid"
        required
      />
      <Input
        label="Client secret"
        type="password"
        placeholder="EHV-…"
        defaultValue="EHV-mock-secret"
        required
      />
      <Input
        label="Webhook ID"
        placeholder="3RX02922TT3949108"
        defaultValue="3RX02922TT3949108"
        required
      />
      <SandboxToggle defaultEnabled={true} />
    </>
  );
}

function ManualCashInfo() {
  return (
    <div className="rounded-md border border-amber-200 dark:border-amber-800/70 bg-amber-50/60 dark:bg-amber-950/30 p-4">
      <div className="flex items-start gap-3">
        <HelpCircle className="w-4 h-4 mt-0.5 text-amber-700 dark:text-amber-300 shrink-0" />
        <div className="text-sm text-amber-900 dark:text-amber-100 leading-relaxed">
          Manual cash entries are recorded by the platform admin via{' '}
          <code className="mono px-1.5 py-0.5 rounded bg-amber-100/80 dark:bg-amber-900/50">
            /admin/cash-entry
          </code>
          . No external configuration.
        </div>
      </div>
    </div>
  );
}

function SandboxToggle({ defaultEnabled }: { defaultEnabled: boolean }) {
  const [enabled, setEnabled] = useState(defaultEnabled);
  return (
    <button
      type="button"
      onClick={() => setEnabled((v) => !v)}
      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border border-zinc-200 dark:border-zinc-700 bg-zinc-50/60 dark:bg-zinc-800/40 hover:bg-zinc-100 dark:hover:bg-zinc-800/70 transition text-left"
      aria-pressed={enabled}
    >
      <span className="text-sm text-zinc-700 dark:text-zinc-200">
        Sandbox mode
      </span>
      <span
        className={cn(
          'relative inline-flex h-5 w-9 rounded-full transition shrink-0',
          enabled
            ? 'bg-amber-500 dark:bg-amber-500'
            : 'bg-zinc-300 dark:bg-zinc-600',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition',
            enabled && 'translate-x-4',
          )}
        />
      </span>
    </button>
  );
}
